import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { PatientService } from "src/app/core/services/patient.service";
import { PatientSearchResult } from "src/app/core/models/patient.model";
import { SearchAppointmentService } from "src/app/core/services/search-appointment.service";
import { SearchRecentAppointments } from "src/app/core/models/search-recent-appointments.model";
import { ApplicationContextService } from "src/app/core/services/application-context.service";
import { Subject, takeUntil } from "rxjs";
import { isCarePlanFamilyTemplateType } from "src/app/core/models/care-plan-template-types";

@Component({
  selector: 'app-template-controls',
  templateUrl: './template-controls.component.html',
  styleUrls: ['./template-controls.component.scss']
})
export class TemplateControlsComponent implements OnInit, OnDestroy {
  @Input() templateType: string = '';
  @Input() visibility: string = 'Only Me';
  @Input() selectedTypes: string[] = [];
  @Input() showDropdown: boolean = false;
  @Input() isDefault: boolean = false;
  @Input() showMakeDefault: boolean = false;
  @Input() sequence: string = 'Prompt';
  @Input() displayVersion: string = '';
  @Input() showPlusButton: boolean = true;
  @Input() contextSubType: string = '';
  @Input() phraseOrPrompt: string = 'phrase';
  @Input() createdByUserId: string = '';
  @Input() currentUserId: string = '';
  @Input() createdBy: string = '';

  currentLoggedInUserName: string = '';
  private readonly destroy$ = new Subject<void>();

  @Output() visibilityChange = new EventEmitter<string>();
  @Output() addAdditionalType = new EventEmitter<string>();
  @Output() removeType = new EventEmitter<string>();
  @Output() toggleDropdown = new EventEmitter<void>();
  @Output() isDefaultChange = new EventEmitter<boolean>();
  @Output() sequenceChange = new EventEmitter<string>();
  @Output() testPatientLinked = new EventEmitter<SearchRecentAppointments | null>();
  @Output() contextSubTypeChange = new EventEmitter<string>();
  @Output() phraseOrPromptChange = new EventEmitter<string>();

  // Test Patient UI state - now for encounters
  testPatient: SearchRecentAppointments | null = null;
  testPatientSearchTerm: string = '';
  isSearchingTestPatient: boolean = false;
  testPatientResults: SearchRecentAppointments[] = [];
  selectedCandidate: SearchRecentAppointments | null = null;
  isDropup=true

  isCarePlanFamily(): boolean {
    return isCarePlanFamilyTemplateType(this.templateType);
  }

  templateSubtypeOptions = [
    'Subjective',
    'Objective',
    'Assessment',
    'Plan'
  ];

  // Plan Sub Type options (shown when Plan is selected)
  planSubTypeOptions = [
    { value: 'Orders', label: 'Orders' },
    { value: 'Procedures', label: 'Procedures' },
    { value: 'Informative', label: 'Informative' },
    { value: 'Referral', label: 'Referral' }
  ];

  // Objective Sub Type options (shown when Objective is selected)
  objectiveSubTypeOptions = [
    { value: 'Exam', label: 'Exam' },
    { value: 'Results', label: 'Results' }
  ];

  constructor(
    private _patientService: PatientService,
    private _searchAppointmentService: SearchAppointmentService,
    private _applicationContextService: ApplicationContextService
  ) {}

  ngOnInit(): void {
    // Subscribe to application context to get current logged-in user name
    this._applicationContextService.context
      .pipe(takeUntil(this.destroy$))
      .subscribe(context => {
        if (context?.currentlyLoggedInUser) {
          const user = context.currentlyLoggedInUser;
          this.currentLoggedInUserName = `${user.firstName} ${user.lastName}`.trim();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onVisibilityChange(visibility: string) {
    this.visibilityChange.emit(visibility);
  }

  onAddAdditionalType(type: string) {
    this.addAdditionalType.emit(type);
  }

  onRemoveType(type: string) {
    this.removeType.emit(type);
  }

  onToggleDropdown() {
    this.toggleDropdown.emit();
  }

  onIsDefaultChange(event: Event) {
    const target = event.target as HTMLInputElement;
    this.isDefaultChange.emit(target.checked);
  }

  onSequenceChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const sequence = target.checked ? 'HTML' : 'Prompt';
    this.sequenceChange.emit(sequence);
  }

  onTestPatientSearch(term: string) {
    this.testPatientSearchTerm = term;
    if (!term || !term.trim()) {
      this.testPatientResults = [];
      this.selectedCandidate = null;
      return;
    }
    
    // Only search if term is at least 3 characters long (same as global-search)
    if (term.trim().length < 3) {
      this.testPatientResults = [];
      this.selectedCandidate = null;
      return;
    }
    
    this.isSearchingTestPatient = true;
    this._searchAppointmentService.getSearchListing(term.trim()).subscribe({
      next: (results) => {
        this.testPatientResults = Array.isArray(results) ? results : [];
        this.isSearchingTestPatient = false;
      },
      error: () => {
        this.testPatientResults = [];
        this.isSearchingTestPatient = false;
      }
    });
  }

  onSelectTestPatient(encounter: SearchRecentAppointments) {
    this.selectedCandidate = encounter;
    const patientName = encounter.patientName || '';
    this.testPatientSearchTerm = patientName;
    // hide results list
    this.testPatientResults = [];
  }

  onSaveTestPatient(dropdownRef: any) {
    if (!this.selectedCandidate) {
      return;
    }
    this.testPatient = this.selectedCandidate;
    this.selectedCandidate = null;
    this.testPatientSearchTerm = '';
    this.testPatientResults = [];
    this.testPatientLinked.emit(this.testPatient);
    dropdownRef?.hide?.();
  }

  onClearTestPatient() {
    this.testPatient = null;
    this.testPatientLinked.emit(null);
  }

  onContextSubTypeChange(subType: string) {
    this.contextSubTypeChange.emit(subType);
  }

  onPhraseOrPromptChange(value: string) {
    this.phraseOrPromptChange.emit(value);
  }

  get shouldShowContextSubType(): boolean {
    const hasPlanOrObjective = this.selectedTypes.includes('Plan') || this.selectedTypes.includes('Objective');
    return (this.templateType === 'Context' || this.templateType === 'Form' || this.templateType === 'Order') && hasPlanOrObjective;
  }

  /** Options for the Sub Type dropdown: Plan options, Objective options, or both when both sections are selected. */
  get contextSubTypeOptions(): { value: string; label: string }[] {
    const hasPlan = this.selectedTypes.includes('Plan');
    const hasObjective = this.selectedTypes.includes('Objective');
    if (hasPlan && hasObjective) {
      return [...this.objectiveSubTypeOptions, ...this.planSubTypeOptions];
    }
    if (hasObjective) {
      return this.objectiveSubTypeOptions;
    }
    return this.planSubTypeOptions;
  }

  get shouldShowPhraseOrPrompt(): boolean {
    return this.templateType === 'Context';
  }

  get filteredTemplateSubtypeOptions(): string[] {
    if (this.templateType === 'Order') {
      return this.templateSubtypeOptions.filter(opt => opt !== 'Plan');
    }
    return this.templateSubtypeOptions;
  }

  get filteredSelectedTypesForOrder(): string[] {
    if (this.templateType === 'Order') {
      return this.selectedTypes.filter(type => type !== 'Plan');
    }
    return this.selectedTypes;
  }

  get isTemplateEditable(): boolean {
    // Compare current logged-in user name with template author name
    if (this.currentLoggedInUserName && this.createdBy) {
      return this.currentLoggedInUserName.trim().toLowerCase() === this.createdBy.trim().toLowerCase();
    }
    return false;
  }

  get isVisibilityEditable(): boolean {
    // Visibility dropdown should always be editable so users can change it back
    return true;
  }
}
